import { useState } from 'react';
import { Plus, Globe, AlertCircle, CheckCircle, Play, Pause } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

interface Webhook {
  id: string;
  name: string;
  url: string;
  events: string[];
  status: 'active' | 'inactive' | 'failed';
  lastTriggered: string;
  deliveries: number;
  successRate: number;
}

const mockWebhooks: Webhook[] = [
  {
    id: '1',
    name: 'Call Events',
    url: 'https://api.company.com/webhooks/calls',
    events: ['call.started', 'call.ended', 'call.failed'],
    status: 'active',
    lastTriggered: '2024-01-24T10:30:00Z',
    deliveries: 1247,
    successRate: 99.2
  },
  {
    id: '2',
    name: 'Bot Status Updates',
    url: 'https://api.company.com/webhooks/bots',
    events: ['bot.deployed', 'bot.error', 'bot.offline'],
    status: 'active',
    lastTriggered: '2024-01-24T09:15:00Z',
    deliveries: 89,
    successRate: 100
  },
  {
    id: '3',
    name: 'Legacy Notifications',
    url: 'https://old-system.company.com/notify',
    events: ['cost.threshold'],
    status: 'failed',
    lastTriggered: '2024-01-23T16:45:00Z',
    deliveries: 456,
    successRate: 67.3
  }
];

const availableEvents = [
  'call.started', 'call.ended', 'call.failed',
  'bot.deployed', 'bot.error', 'bot.offline',
  'cost.threshold', 'cost.anomaly',
  'metric.alert', 'system.error'
];

export function WebhookConfiguration() {
  const [webhooks] = useState(mockWebhooks);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newWebhook, setNewWebhook] = useState({
    name: '',
    url: '',
    events: [] as string[],
    secret: ''
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'inactive':
        return <Pause className="w-4 h-4 text-gray-500" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      active: 'default',
      inactive: 'secondary',
      failed: 'destructive'
    };
    return variants[status as keyof typeof variants] || 'outline';
  };

  const testWebhook = (id: string) => {
    console.log('Testing webhook:', id);
  };

  const toggleWebhook = (id: string) => {
    console.log('Toggling webhook:', id);
  };

  const createWebhook = () => {
    console.log('Creating webhook:', newWebhook);
    setIsCreateDialogOpen(false);
    setNewWebhook({ name: '', url: '', events: [], secret: '' });
  };

  return (
    <div className="space-y-6">
      {/* Webhooks List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Webhook Endpoints</CardTitle>
              <div className="text-sm text-muted-foreground">
                Configure endpoints to receive real-time event notifications
              </div>
            </div>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Webhook
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Add Webhook Endpoint</DialogTitle>
                  <DialogDescription>
                    Create a new webhook to receive event notifications.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="webhookName">Name</Label>
                    <Input
                      id="webhookName"
                      value={newWebhook.name}
                      onChange={(e) => setNewWebhook(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Call Events Handler"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="webhookUrl">Endpoint URL</Label>
                    <Input
                      id="webhookUrl"
                      value={newWebhook.url}
                      onChange={(e) => setNewWebhook(prev => ({ ...prev, url: e.target.value }))}
                      placeholder="https://api.company.com/webhooks/events"
                    />
                  </div>
                  
                  <div>
                    <Label>Events</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
                      {availableEvents.map((event) => (
                        <label key={event} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={newWebhook.events.includes(event)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewWebhook(prev => ({
                                  ...prev,
                                  events: [...prev.events, event]
                                }));
                              } else {
                                setNewWebhook(prev => ({
                                  ...prev,
                                  events: prev.events.filter(e => e !== event)
                                }));
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{event}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="secret">Secret (Optional)</Label>
                    <Input
                      id="secret"
                      type="password"
                      value={newWebhook.secret}
                      onChange={(e) => setNewWebhook(prev => ({ ...prev, secret: e.target.value }))}
                      placeholder="Webhook secret for verification"
                    />
                  </div>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={createWebhook} 
                    disabled={!newWebhook.name || !newWebhook.url || newWebhook.events.length === 0}
                  >
                    Create Webhook
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {webhooks.map((webhook) => (
              <Card key={webhook.id} className="p-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Globe className="w-4 h-4 text-blue-500" />
                      <div>
                        <div className="font-medium text-sm">{webhook.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">
                          {webhook.url}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(webhook.status)}
                      <Badge variant={getStatusBadge(webhook.status) as any}>
                        {webhook.status}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-1">
                    {webhook.events.map((event) => (
                      <Badge key={event} variant="outline" className="text-xs">
                        {event}
                      </Badge>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="space-x-4">
                      <span>Last triggered: {formatDate(webhook.lastTriggered)}</span>
                      <span>Deliveries: {webhook.deliveries.toLocaleString()}</span>
                      <span className={webhook.successRate > 95 ? 'text-green-600' : webhook.successRate > 80 ? 'text-yellow-600' : 'text-red-600'}>
                        Success: {webhook.successRate}%
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => testWebhook(webhook.id)}>
                        <Play className="w-3 h-3 mr-1" />
                        Test
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => toggleWebhook(webhook.id)}>
                        {webhook.status === 'active' ? 'Disable' : 'Enable'}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Webhook Deliveries */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Deliveries</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Webhook</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Response Time</TableHead>
                <TableHead>Timestamp</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>Call Events</TableCell>
                <TableCell><Badge variant="outline">call.started</Badge></TableCell>
                <TableCell><Badge variant="default">200 OK</Badge></TableCell>
                <TableCell className="font-mono">124ms</TableCell>
                <TableCell>2024-01-24 10:30:15</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Bot Status Updates</TableCell>
                <TableCell><Badge variant="outline">bot.deployed</Badge></TableCell>
                <TableCell><Badge variant="default">200 OK</Badge></TableCell>
                <TableCell className="font-mono">89ms</TableCell>
                <TableCell>2024-01-24 09:15:23</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Legacy Notifications</TableCell>
                <TableCell><Badge variant="outline">cost.threshold</Badge></TableCell>
                <TableCell><Badge variant="destructive">404 Not Found</Badge></TableCell>
                <TableCell className="font-mono">--</TableCell>
                <TableCell>2024-01-23 16:45:12</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Webhook Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Global Webhook Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="retryAttempts">Retry Attempts</Label>
              <Select defaultValue="3">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 attempt</SelectItem>
                  <SelectItem value="3">3 attempts</SelectItem>
                  <SelectItem value="5">5 attempts</SelectItem>
                  <SelectItem value="10">10 attempts</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="timeout">Timeout (seconds)</Label>
              <Select defaultValue="30">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 seconds</SelectItem>
                  <SelectItem value="30">30 seconds</SelectItem>
                  <SelectItem value="60">60 seconds</SelectItem>
                  <SelectItem value="120">120 seconds</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <Label htmlFor="userAgent">User Agent</Label>
            <Input
              id="userAgent"
              defaultValue="AI-Cost-Control-Center/1.0"
              placeholder="Custom user agent"
            />
          </div>
          
          <Button>Save Configuration</Button>
        </CardContent>
      </Card>
    </div>
  );
}